<template>
  <div class="flex flex-col gap-5">
    <TitleWrapper
      :filter-option="false"
      title="Billing_Payment"
      tooltip-title="Billing_Payment"
      :display-breadcrumb="false"
    />

    <div class="flex flex-col gap-6">
      <DetailCard :details="userDetials" class="mt-5" />
      <div class="flex flex-col md:flex-row justify-between">
        <div class="text-xl font-inter font-medium text-black">Subscription Plans</div>
        <div class="flex gap-5 items-center">
          <ToggleButton v-model="subscriptionPlansType" heading="Monthly" :is-status-hide="true" />
          <div>
            Yearly
            <span class="text-primary-purple-800">( 17% OFF )</span>
          </div>
        </div>
      </div>

      <Loader v-if="isLoading" class="mt-20 mb-40" :content="true" />
      <NoRecordFound v-else-if="noRecord" />

      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4"
      >
        <div
          v-for="plan in displayedPlans"
          :key="plan.id || plan.name"
          class="p-[30px] rounded-[19px]"
          :class="
            plan.active
              ? 'bg-primary-purple-100 text-primary-gray-800'
              : 'bg-bg-color-white text-text-grey'
          "
        >
          <div class="flex gap-[30px] flex-col items-center w-full">
            <div class="flex gap-2.5 flex-col items-center">
              <div
                class="font-inter gap-2 font-semibold text-3xl text-primary-purple-800 leading-[42px]"
              >
                {{ plan.name }}
              </div>
              <div class="flex items-center gap-2">
                <Icon
                  icon="user"
                  height="20"
                  width="20"
                  :color="plan.active ? 'primary-gray-600' : 'primary-purple-600'"
                />
                <p
                  :class="plan.active ? 'text-primary-gray-600' : 'text-text-grey'"
                  class="text-base leading-7 font-medium"
                >
                  <span v-if="isRegularPlan(plan)">
                    Up to {{ plan.allowed_student_to }} Students
                  </span>
                  <span v-else>1001 Students and above</span>
                </p>
              </div>
            </div>

            <div
              v-if="isRegularPlan(plan)"
              :class="plan.active ? 'text-primary-gray-700' : 'text-primary-purple-600'"
              class="text-4xl font-bold"
            >
              {{ plan.charge_amount }}
              <span v-if="plan.charge_amount && plan.charge_interval">/</span>
              <span
                :class="plan.active ? 'text-primary-gray-700' : 'text-primary-purple-600'"
                class="text-xs lg:text-sm font-extrabold"
              >
                {{ plan.charge_interval }}
              </span>
            </div>

            <div>
              <UIButton
                v-if="getButtonText(plan)"
                button="primary"
                class="px-2.5"
                :disabled="isLoading"
                @click="subscriptionHandler(plan)"
              >
                {{ getButtonText(plan) }}
              </UIButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PaymentContactUsModal v-if="showModal" :modal="showModal" @toggle="toggleContactUsModal" />
  </div>
</template>

<script>
import TitleWrapper from '@components/TitleWrapper.vue'
import Icon from '@components/icons/icon.vue'
import { mapActions } from 'vuex'
import Loader from '@components/BaseComponent/Loader.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import PaymentContactUsModal from '@/src/router/views/payment-methods/modals/PaymentContactUsModal.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import DetailCard from '@/src/components/UiElements/DetailCard.vue'
import ToggleButton from '@src/components/BaseComponent/toggleButton.vue'

export default {
  components: {
    TitleWrapper,
    Icon,
    NoRecordFound,
    Loader,
    PaymentContactUsModal,
    UIButton,
    DetailCard,
    ToggleButton,
  },
  data() {
    return {
      subscriptionPlansType: false,
      showModal: false,
      createdStudentCount: 0,
      isLoading: false,
      monthlyPlans: [],
      yearlyPlans: [],
      userDetials: {},
      currentPlanId: null,
      currentStudentLimit: 0,
      selectedPlanIndex: 0,
      customSubscriptionPlan: { name: 'Custom Plan', allowed_student_to: 1001 },
      freePlan: { name: 'Free', description: 'Up to 50 student for lifetime', charge_amount: '$0' },
    }
  },
  computed: {
    selectedPlan() {
      return this.billingPlans[this.selectedPlanIndex] || null
    },
    displayedPlans() {
      return this.subscriptionPlansType ? this.yearlyPlans : this.monthlyPlans
    },
    isRegularPlan() {
      return (plan) => plan.name !== 'Custom Plan'
    },
    getButtonText() {
      return (plan) => {
        if (plan.name === 'Custom Plan') return 'Contact Us'
        if (!this.currentPlanId) return 'Buy'
        if (plan.id === this.currentPlanId && plan.active) return 'Manage Subscription'

        const currentPlan = [...this.monthlyPlans, ...this.yearlyPlans].find(
          (plan) => plan.id === this.currentPlanId,
        )

        if (currentPlan && Number(plan.charge_amount) > Number(currentPlan.charge_amount)) {
          return 'Upgrade Subscription'
        }
        if (currentPlan && Number(plan.charge_amount) < Number(currentPlan.charge_amount)) {
          return 'Downgrade Subscription'
        }
        return ''
      }
    },
    noRecord() {
      return !this.displayedPlans.length
    },
  },
  created() {
    this.getSubsriptionPlans()

    this.getUserCountDetails()
  },
  methods: {
    async getUserCountDetails() {
      let [response, error] = await this.getUserCount()
      this.userDetials = {
        totalStudents: response?.data?.students_count,
        totalUsers: response?.data?.all_users,
        otherUsers: response?.data?.users_other_than_student,
      }
    },
    toggleContactUsModal() {
      this.showModal = !this.showModal
    },

    subscriptionHandler(plan) {
      if (plan.name === 'Custom Plan') {
        this.toggleContactUsModal()
        return
      }
      plan.active || this.currentStudentLimit
        ? this.upgradeSubscriptionPlan()
        : this.createSubscriptionPlan(plan)
    },

    async createSubscriptionPlan(plan) {
      const [res] = await this.createSubscription({ subscription_plan_id: plan.id })
      if (res?.data?.checkout_url) window.location.href = res.data.checkout_url
    },

    async upgradeSubscriptionPlan() {
      const [res] = await this.upgradeSubscription()
      if (res?.data?.checkout_url) window.open(res.data.checkout_url, '_blank')
    },

    async getSubsriptionPlans() {
      this.isLoading = true
      const [res] = await this.getAllSubsriptionPlans()

      if (res) {
        const plans = res?.data?.records || []

        this.createdStudentCount = res?.data?.student_count

        this.monthlyPlans = plans.filter((plan) => plan.charge_interval === 'month')
        this.yearlyPlans = plans.filter((plan) => plan.charge_interval === 'year')

        this.monthlyPlans.push(this.customSubscriptionPlan)
        this.monthlyPlans.unshift(this.freePlan)

        this.yearlyPlans.push(this.customSubscriptionPlan)
        this.yearlyPlans.unshift(this.freePlan)

        const activePlan = plans.find((plan) => plan.active)
        if (activePlan) {
          this.currentPlanId = activePlan.id
          this.currentStudentLimit = activePlan.allowed_student_to || 0

          this.subscriptionPlansType = activePlan.charge_interval === 'year'
        }
      }
      this.isLoading = false
    },

    ...mapActions('paymentMethods', [
      'getAllSubsriptionPlans',
      'createSubscription',
      'upgradeSubscription',
      'getUserCount',
    ]),
  },
}
</script>
